import { useEffect, useRef, useState } from 'react';
import type { EstadoFiltro } from '@/hooks/useFiltrosCompromisos';
import styles from './MultiSelectChip.module.css';

interface Props {
  value: EstadoFiltro;
  onChange: (next: EstadoFiltro) => void;
  counts: Record<EstadoFiltro, number>;
}

interface OpcionEstado {
  id: EstadoFiltro;
  label: string;
  icon: string;
  description: string;
  /** Clase de chip cuando este estado está activo. */
  activeClass: string;
}

const OPCIONES: OpcionEstado[] = [
  {
    id: 'pendientes',
    label: 'Pendientes',
    icon: '📋',
    description: 'No completados (pendientes, on track o bloqueados).',
    activeClass: 'chipPrimary',
  },
  {
    id: 'bloqueados',
    label: 'Bloqueados',
    icon: '🚫',
    description: 'Con al menos un ítem bloqueado.',
    activeClass: 'chipDanger',
  },
  {
    id: 'completados',
    label: 'Completados',
    icon: '✓',
    description: 'Todos los ítems entregados.',
    activeClass: 'chipSuccess',
  },
  {
    id: 'todos',
    label: 'Todos',
    icon: '📂',
    description: 'Sin filtrar por estado.',
    activeClass: 'chipActive',
  },
];

/**
 * Chip de estado — único entre las 4 opciones (pendientes / bloqueados /
 * completados / todos). Visualmente sigue siendo un chip activo (azul, rojo,
 * verde, gris). Click abre dropdown con opciones radio + contador.
 */
export function EstadoFiltroChip({ value, onChange, counts }: Props) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    function onClick(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const current = OPCIONES.find((o) => o.id === value) || OPCIONES[0];

  return (
    <div className={styles.wrapper} ref={wrapperRef}>
      <button
        type="button"
        className={`${styles.chip} ${styles[current.activeClass]}`}
        onClick={() => setOpen((v) => !v)}
      >
        <span className={styles.chipIcon}>{current.icon}</span>
        <span>{current.label}</span>
        <span className={styles.chipCount}>{counts[value] ?? 0}</span>
        <span style={{ fontSize: 11, opacity: 0.85 }}>▾</span>
      </button>

      {open && (
        <div className={styles.dropdown} style={{ minWidth: 240 }}>
          <div className={styles.dropdownList}>
            {OPCIONES.map((o) => {
              const isCurrent = o.id === value;
              return (
                <label
                  key={o.id}
                  className={styles.dropdownItem}
                  style={isCurrent ? { background: 'var(--surface-2)' } : undefined}
                >
                  <input
                    type="radio"
                    name="estado-filtro"
                    checked={isCurrent}
                    onChange={() => {
                      onChange(o.id);
                      setOpen(false);
                    }}
                    style={{ accentColor: 'var(--brand)' }}
                  />
                  <span style={{ fontSize: 14 }}>{o.icon}</span>
                  <span className={styles.dropdownItemLabel}>
                    <div style={{ fontWeight: 600 }}>
                      {o.label}{' '}
                      <span style={{ color: 'var(--text-soft)', fontWeight: 500 }}>
                        · {counts[o.id] ?? 0}
                      </span>
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--text-soft)' }}>
                      {o.description}
                    </div>
                  </span>
                </label>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
